import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { MessageCircle, CheckCircle, Clock, Send, Tag, Phone, Share2 } from 'lucide-react';
import { formatWhatsAppNumber } from '../../utils/phoneUtils';
import { addMessageToTicket, updateTicketStatus } from '../../services/ticketService';
import { getAllUsers } from '../../services/userService';
import { canViewBranchData } from '../../utils/branchUtils';

export default function TicketManagement() {
  const { userProfile } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const activeTicket = tickets.find(t => t.id === activeTicketId) || null;
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('open');
  const [allAdmins, setAllAdmins] = useState([]);
  const [showForwardMenu, setShowForwardMenu] = useState(false);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTicket?.messages]);

  useEffect(() => {
    if (!userProfile) return;

    if (userProfile.role === 'superAdmin') {
      getAllUsers().then(users => setAllAdmins(users.filter(u => u.phone))).catch(e => console.error(e));
    }

    let q;
    if (userProfile.role === 'superAdmin') {
      q = query(collection(db, 'tickets'));
    } else {
      if (!userProfile.assignedBranches || userProfile.assignedBranches.length === 0) {
        setTickets([]);
        setLoading(false);
        return;
      }
      // Tüm biletleri önce çekip snapshot'ta filtreleyeceğiz, 
      // çünkü Firestore "in" sorgusuyla tüm diziyi filtreliyor ve alt-üst şube ayrımı yapamıyor.
      q = query(collection(db, 'tickets'));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let allTickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Süper admin değilse, Helper Fonksiyonuyla yetki dışı şubeleri gizle
      if (userProfile.role !== 'superAdmin') {
         // Ticket'larin `branchId` alani var
         allTickets = allTickets.filter(t => canViewBranchData(userProfile, t.branchId, userProfile.systemBranches || []));
      }
      // Sort by latest message/update
      allTickets.sort((a, b) => new Date(b.updatedAt?.seconds ? b.updatedAt.toDate() : b.updatedAt) - new Date(a.updatedAt?.seconds ? a.updatedAt.toDate() : a.updatedAt));
      setTickets(allTickets);
      
      setLoading(false);
    }, (error) => {
       console.error("Bilet çekme hatası: ", error);
       setLoading(false);
    });

    return () => unsubscribe();
  }, [userProfile]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || isSubmitting || !activeTicket) return;

    setIsSubmitting(true);
    try {
      await addMessageToTicket(activeTicket.id, {
        senderRole: 'admin',
        senderName: `${userProfile.displayName || 'Yetkili'}`,
        text: replyText.trim()
      });
      // Optionally reset status to open if it was resolved
      if (activeTicket.status === 'resolved') {
        await updateTicketStatus(activeTicket.id, 'open');
      }
      setReplyText('');
    } catch (err) {
      alert("Cevap gönderilemedi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const markAsResolved = async () => {
    if (!activeTicket) return;
    try {
      await updateTicketStatus(activeTicket.id, activeTicket.status === 'resolved' ? 'open' : 'resolved');
    } catch (err) {
      alert("Durum güncellenemedi.");
    }
  };

  const handleForward = (adminUser) => {
    if (!activeTicket || !adminUser.phone) return;
    
    const customerMsg = activeTicket.messages.find(m => m.senderRole === 'customer') || activeTicket.messages[0];
    
    const text = `${activeTicket.customerName} isimli müşteri sizinle şu konuda görüşmek istiyor:

Müşteri Mesajı: ${customerMsg?.text || ''}
Müşteri Adı: ${activeTicket.customerName}
Telefon Numarası: ${activeTicket.customerPhone}`;

    const waLink = `https://wa.me/${formatWhatsAppNumber(adminUser.phone)}?text=${encodeURIComponent(text)}`;
    window.open(waLink, '_blank');
    setShowForwardMenu(false);
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col md:flex-row gap-6">
      
      {/* Sol Panel: Bilet Listesi */}
      <div className="w-full md:w-1/3 flex flex-col glass rounded-2xl border border-navy-700/50 overflow-hidden">
        <div className="p-4 bg-navy-800/50 border-b border-navy-700/50 bg-gradient-to-r from-navy-800 to-navy-900">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-forest-400" />
            Müşteri Mesajları
          </h2>
          <p className="text-xs text-navy-400 mt-1">Sorumlu olduğunuz şubeye gelen talepler</p>
        </div>

        <div className="flex bg-navy-900 border-b border-navy-700/50">
           <button onClick={() => setActiveTab('open')} className={`flex-1 py-3 text-xs font-bold transition-colors ${activeTab === 'open' ? 'border-b-2 border-forest-500 text-white bg-navy-800' : 'text-navy-400 hover:text-navy-300'}`}>
              Bekleyen ({tickets.filter(t=>t.status==='open').length})
           </button>
           <button onClick={() => setActiveTab('resolved')} className={`flex-1 py-3 text-xs font-bold transition-colors ${activeTab === 'resolved' ? 'border-b-2 border-forest-500 text-white bg-navy-800' : 'text-navy-400 hover:text-navy-300'}`}>
              Arşivlenmiş ({tickets.filter(t=>t.status!=='open').length})
           </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
             <div className="text-center text-navy-400 italic py-10">Yükleniyor...</div>
          ) : tickets.length === 0 ? (
             <div className="text-center text-navy-400 py-10 text-sm">Gelen mesaj bulunmamakta.</div>
          ) : (
            (() => {
              const filteredTickets = tickets.filter(t => activeTab === 'open' ? t.status === 'open' : t.status !== 'open');
              
              if (filteredTickets.length === 0) {
                 return <div className="text-center text-navy-400 py-10 text-sm">Bu klasörde mesaj bulunmamakta.</div>;
              }

              return filteredTickets.map(ticket => {
                const lastMsg = ticket.messages[ticket.messages.length - 1];
                const isUnread = lastMsg?.senderRole === 'customer'; // Basit okunmamış mantığı (son mesaj ondan ise)

                return (
                  <div 
                    key={ticket.id} 
                    onClick={() => setActiveTicketId(ticket.id)}
                    className={`p-4 rounded-xl cursor-pointer transition-all border ${
                      activeTicket?.id === ticket.id 
                        ? 'bg-navy-800 border-forest-500 shadow-lg shadow-forest-500/10' 
                        : 'bg-navy-900/50 border-navy-700/50 hover:bg-navy-800'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-white text-sm truncate flex-1">{ticket.customerName}</h3>
                      {ticket.status === 'resolved' ? (
                         <CheckCircle className="w-4 h-4 text-green-500 ml-2 flex-shrink-0" />
                      ) : isUnread ? (
                         <span className="w-2.5 h-2.5 bg-red-500 rounded-full ml-2 flex-shrink-0 animate-pulse shadow-sm shadow-red-500"></span>
                      ) : (
                         <span className="w-2.5 h-2.5 bg-forest-500 rounded-full ml-2 flex-shrink-0"></span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-navy-400 mb-2">
                      <Tag className="w-3 h-3" /> Küpe: {ticket.animalTagNo}
                      <span className="text-navy-600 px-1">•</span>
                      Şube: {String(ticket.branchId || 'Belirsiz').toUpperCase()}
                    </div>
                    <p className="text-xs text-navy-300 truncate opacity-80">
                      {lastMsg?.text}
                    </p>
                  </div>
                );
              });
            })()
          )}
        </div>
      </div>

      {/* Sağ Panel: Chat Alanı */}
      <div className="flex-1 flex flex-col glass rounded-2xl border border-navy-700/50 overflow-hidden relative">
        {!activeTicket ? (
           <div className="flex-1 flex flex-col items-center justify-center text-navy-500">
              <MessageCircle className="w-16 h-16 mb-4 opacity-50" />
              <p className="font-medium text-lg text-navy-300">Mesaj seçilmedi</p>
              <p className="text-sm">Sol menüden bir sohbet penceresine tıklayın.</p>
           </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="p-4 bg-navy-800/80 border-b border-navy-700/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-white">{activeTicket.customerName}</h3>
                <div className="flex items-center gap-3 text-xs text-navy-400 mt-1">
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {activeTicket.customerPhone}</span>
                  <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> Küpe: {activeTicket.animalTagNo}</span>
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-navy-900/80 rounded border border-navy-700/50 uppercase font-bold tracking-wider text-[10px]">
                     {String(activeTicket.branchId || 'BELİRSİZ')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {userProfile?.role === 'superAdmin' && (
                   <div className="relative">
                      <button
                         onClick={() => setShowForwardMenu(!showForwardMenu)}
                         className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-all shadow-lg flex items-center gap-2 border border-blue-500/50"
                      >
                         <Share2 className="w-4 h-4" /> Yönlendir
                      </button>
                      
                      {showForwardMenu && (
                         <>
                            {/* Backdrop to close menu when clicked outside */}
                            <div className="fixed inset-0 z-40" onClick={() => setShowForwardMenu(false)}></div>
                            <div className="absolute right-0 mt-2 w-72 bg-navy-800 border border-navy-600 rounded-xl shadow-2xl z-50 overflow-hidden select-none">
                               <div className="p-3 border-b border-navy-700 bg-navy-900">
                                  <p className="text-xs text-navy-300 font-bold uppercase tracking-wider">Kime Yönlendirilecek?</p>
                               </div>
                               <div className="max-h-64 overflow-y-auto custom-scrollbar">
                                  {allAdmins.map(adminUser => (
                                     <button
                                        key={adminUser.id}
                                        onClick={() => handleForward(adminUser)}
                                        className="w-full text-left px-4 py-3 hover:bg-navy-700 border-b border-navy-700/50 transition-colors flex flex-col group relative z-50"
                                     >
                                        <span className="text-sm font-bold text-white group-hover:text-gold-400">{adminUser.displayName || adminUser.username}</span>
                                        <span className="text-[10px] text-navy-400 mt-1 uppercase font-semibold">{adminUser.role === 'superAdmin' ? 'Süper Admin' : adminUser.role === 'admin' ? 'Ana Şube' : 'Alt Şube'} - {adminUser.phone}</span>
                                     </button>
                                  ))}
                                  {allAdmins.length === 0 && <div className="p-4 text-xs text-center text-navy-400">Telefon numarası kayıtlı yönetici bulunamadı.</div>}
                               </div>
                            </div>
                         </>
                      )}
                   </div>
                )}
                <button 
                  onClick={markAsResolved}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                    activeTicket.status === 'resolved' 
                    ? 'border-navy-500 text-navy-300 hover:bg-navy-800' 
                    : 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                  }`}
                >
                  {activeTicket.status === 'resolved' ? 'Tekrar Aç' : 'Çözüldü Olarak İşaretle'}
                </button>
                <a 
                  href={`https://wa.me/${formatWhatsAppNumber(activeTicket.customerPhone)}?text=Merhaba ${activeTicket.customerName}, Hasımoğlu Çiftliğinden yazıyoruz.`}
                  target="_blank" rel="noreferrer"
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-xs font-bold transition-all shadow-lg"
                >
                  WhatsApp'tan Dön
                </a>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-navy-950/40">
              {activeTicket.messages?.map((msg, idx) => {
                const isAdmin = msg.senderRole === 'admin';
                return (
                  <div key={idx} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-5 py-3 shadow-md ${
                      isAdmin 
                        ? 'bg-forest-600 text-white rounded-br-sm' 
                        : 'bg-navy-800 text-white rounded-bl-sm border border-navy-700'
                    }`}>
                       {!isAdmin && <div className="text-[11px] font-bold text-gold-400 mb-1.5">{msg.senderName}</div>}
                       <p className="text-sm leading-relaxed">{msg.text}</p>
                       <span className="text-[10px] opacity-60 mt-2 block text-right">
                         {new Date(msg.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute:'2-digit' })}
                       </span>
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            {activeTicket.status === 'resolved' ? (
              <div className="p-4 bg-navy-900 border-t border-navy-700/50 text-center text-sm text-navy-400 py-6">
                 Bu destek talebi kapatılmıştır. Yanıt yazmak için tekrar açıtın.
              </div>
            ) : (
              <form onSubmit={handleSendReply} className="p-4 bg-navy-900 border-t border-navy-700/50 flex gap-3">
                 <input 
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Yanıtınızı yazın..."
                    className="flex-1 bg-navy-950 border border-navy-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-forest-500"
                 />
                 <button 
                    type="submit"
                    disabled={!replyText.trim() || isSubmitting}
                    className="px-6 bg-forest-600 hover:bg-forest-500 disabled:opacity-50 text-white rounded-xl shadow-lg transition-all flex items-center justify-center"
                 >
                    <Send className="w-5 h-5" />
                 </button>
              </form>
            )}
            
          </>
        )}
      </div>
    </div>
  );
}
